/**
 * Subscription state, derived in one place.
 *
 * The payment provider owns the billing calendar, so the subscription status only says
 * whether a subscription is live -- not whether the user may use paid features, and not
 * whether it will renew. Those are separate facts:
 *
 *   may use paid features -> executionLicense
 *   will it renew         -> subscriptionCancelAtPeriodEnd
 *   until when            -> subscriptionCurrentPeriodEnd
 *
 * Statuses are Pending | Active | Ended. "Cancelled", "Deleted" and "Error" no longer
 * exist, and Active covers both "will renew" and "cancelled but still inside the period
 * the user paid for" -- which is why nothing here compares a status to decide access.
 */

export const SubscriptionStatus = {
  Pending: "Pending",
  Active: "Active",
  Ended: "Ended",
};

/**
 * The only thing that may gate a paid feature. A user can be Freemium with an Active
 * subscription (first payment not recorded yet) and Premium with an Ended one (cancelled
 * but paid through the current period); both are normal.
 */
export const isPremium = (userInfo) => userInfo?.executionLicense === "Premium";

export const isEnterprise = (userInfo) =>
  userInfo?.executionLicense === "Enterprise";

/** True once user info has actually been loaded, so the UI can avoid flashing. */
export const isUserInfoLoaded = (userInfo) =>
  !!userInfo && userInfo.executionLicense !== undefined;

/** A live subscription exists at the provider. Says nothing about entitlement. */
export const isSubscriptionLive = (userInfo) =>
  userInfo?.subscriptionStatus === SubscriptionStatus.Active;

/** Live, but cancelled -- there will be no further payment. Was: status === "Cancelled". */
export const isSubscriptionEnding = (userInfo) =>
  isSubscriptionLive(userInfo) &&
  userInfo?.subscriptionCancelAtPeriodEnd === true;

/**
 * The current period has already passed while still marked live. Mirrors the backend's
 * PaymentService.HasNothingLeftToCollect: the provider has stopped collecting in every way
 * that matters even though the webhook that would close the row locally (dunning exhaustion,
 * or Ended) hasn't landed yet. Subscribe() already knows to end a row in this state before
 * starting a fresh one, so the UI must stop treating it as renewing or the customer is left
 * with only Cancel and no way back in.
 */
const hasCurrentPeriodElapsed = (userInfo) =>
  !!userInfo?.subscriptionCurrentPeriodEnd &&
  new Date(userInfo.subscriptionCurrentPeriodEnd) <= new Date();

/** Live and will be charged again. Was: status === "Active". */
export const isSubscriptionRenewing = (userInfo) =>
  isSubscriptionLive(userInfo) &&
  !userInfo?.subscriptionCancelAtPeriodEnd &&
  !hasCurrentPeriodElapsed(userInfo);

/** An order exists but checkout never completed. */
export const isPaymentPending = (userInfo) =>
  userInfo?.subscriptionStatus === SubscriptionStatus.Pending;

/**
 * The date the current period runs to: the next billing date while it renews, or the date
 * access ends once cancelled. Falls back to the licence expiration for users whose licence
 * was granted by hand and so have no subscription row.
 */
export const subscriptionPeriodEnd = (userInfo) =>
  userInfo?.subscriptionCurrentPeriodEnd ?? userInfo?.licenseExpiration ?? null;

/**
 * A payment needs the customer's attention right now (a decline, or the bank asking for
 * authentication) and the provider gave us a link that resolves it directly. Presence of the
 * link is the signal -- it is set and cleared together with the failing state server side, so
 * there is nothing else to check here.
 */
export const paymentActionUrl = (userInfo) =>
  userInfo?.subscriptionPaymentActionUrl ?? null;

/** Enterprise licences are managed outside the app, so they get no subscription UI. */
export const canSubscribe = (userInfo) =>
  isUserInfoLoaded(userInfo) && !isEnterprise(userInfo);

/**
 * Nothing to buy: a live subscription already renews by itself, and the backend refuses a
 * second one. Deliberately not tied to the licence -- a live subscription can coexist with an
 * expired licence, and offering a purchase there would only be refused.
 */
export const hasNothingToBuy = (userInfo) => isSubscriptionRenewing(userInfo);

/**
 * The seven fields the backend renders the invoice from. It refuses to create an order
 * unless all of them are present, so check here rather than on the hasBillingAddress
 * flag -- that flag only means *an* address is stored, not a complete one.
 */
export const BILLING_ADDRESS_FIELDS = [
  "companyName",
  "address",
  "postalCode",
  "city",
  "country",
  "email",
  "vatNumber",
];

export const isBillingAddressComplete = (billingAddress) =>
  !!billingAddress &&
  BILLING_ADDRESS_FIELDS.every(
    (field) =>
      billingAddress[field] !== null &&
      billingAddress[field] !== undefined &&
      `${billingAddress[field]}`.trim() !== "",
  );
